import { useCallback, useEffect, useRef, useState } from "react";
import qlog from "views_shared/app/javascript/scripts/qlog";
import { isChecklistComplete } from "./ChecklistCard";

const HIDDEN_KEY_PREFIX = "qwoted.profileChecklistHidden";

// localStorage can throw (privacy modes, disabled storage); then a hide lasts this page view only.
function readHidden(key) {
  try {
    return globalThis.localStorage?.getItem(key) === "true";
  } catch {
    return false;
  }
}

function writeHidden(key, hidden) {
  try {
    if (hidden) globalThis.localStorage?.setItem(key, "true");
    else globalThis.localStorage?.removeItem(key);
  } catch {
    // Storage unavailable: the in-memory state still applies.
  }
}

/**
 * useProfileCompletion — data half of the profile's "Checklist" rail card (the chrome half is
 * ChecklistCard). Model-agnostic: the caller hands in which client to read through
 * (`qapi.expertCompletionScores` or `qapi.prProfileCompletionScores`) and which id to read, so this
 * hook itself knows nothing about experts vs PR profiles.
 *
 * Idles (settling to `null`, so the card can collapse) until it has an `id` — mirrors
 * useTeamMembers/useMediaOutletsRail's shape. Never throws: this is an owner-facing but still
 * decorative widget, not a form, so a failed read should render nothing rather than an editor
 * acting on a fabricated "all incomplete" score — both failure shapes (`getResource` resolving
 * `{ error }` on a non-2xx, and a rejected transport promise) log and settle `null`.
 *
 * @param {Object} options
 * @param {{ getById: Function }} options.resourceClient - The completion-scores API client to read
 *   through (e.g. `qapi.expertCompletionScores`)
 * @param {number|string} [options.id] - The expert or PR-profile id whose completion score to load
 * @param {string} options.kind - The profile type ("expert", "pr_profile", ...), keying the hidden flag
 * @param {*} [options.refreshKey] - Re-reads the score, without a skeleton, whenever this changes
 * @returns {{ score: Object|null, isLoading: boolean, hidden: boolean, hide: Function, refresh: Function }} `score` is
 *   the raw completion-scores view row (`completion_score` plus the `has_*` boolean factor columns),
 *   or `null` while idle, loading, or after a failed read. `hidden` is true once Hide Checklist was
 *   used on this profile in this browser, until the checklist is incomplete again.
 */
function useProfileCompletion({ resourceClient, id, kind, refreshKey }) {
  const [score, setScore] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const loadedIdRef = useRef(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const refresh = useCallback(() => setRefreshCount((count) => count + 1), []);
  const hiddenKey = `${HIDDEN_KEY_PREFIX}.${kind}.${id}`;
  const [hiddenFlag, setHiddenFlag] = useState(() => Boolean(id) && readHidden(hiddenKey));

  useEffect(() => {
    setHiddenFlag(Boolean(id) && readHidden(hiddenKey));
  }, [hiddenKey, id]);

  const incomplete = Boolean(score) && !isChecklistComplete(score);
  useEffect(() => {
    if (incomplete && hiddenFlag) {
      writeHidden(hiddenKey, false);
      setHiddenFlag(false);
    }
  }, [incomplete, hiddenFlag, hiddenKey]);

  const hide = useCallback(() => {
    writeHidden(hiddenKey, true);
    setHiddenFlag(true);
  }, [hiddenKey]);

  useEffect(() => {
    // No id yet (profile still loading): settle null rather than leaving the card in a skeleton
    // that never resolves.
    if (!id) {
      loadedIdRef.current = null;
      setScore(null);
      setIsLoading(false);
      return undefined;
    }

    let componentIsMounted = true;
    const silent = loadedIdRef.current === id;

    const load = async () => {
      try {
        if (!silent) setIsLoading(true);

        const response = await resourceClient.getById(id);
        if (!componentIsMounted) return;

        // getResource RESOLVES `{ error }` on a non-2xx instead of throwing, so THIS branch — not
        // the catch below — is the one an ordinary PostgREST failure takes.
        if (response?.error) {
          qlog.error("Profile completion lookup failed:", response.error);
          if (!silent) setScore(null);
          return;
        }

        const rows = Array.isArray(response?.data) ? response.data : [];
        setScore(rows[0] ?? null);
        loadedIdRef.current = id;
      } catch (error) {
        // The transport itself failed (network down, token refresh threw) — a rejection, not `{ error }`.
        qlog.error("Profile completion lookup failed:", error);
        if (componentIsMounted && !silent) setScore(null);
      } finally {
        if (componentIsMounted) setIsLoading(false);
      }
    };
    load();

    return () => {
      componentIsMounted = false;
    };
  }, [resourceClient, id, refreshKey, refreshCount]);

  return { score, isLoading, hidden: hiddenFlag && !incomplete, hide, refresh };
}

export default useProfileCompletion;
